/**
 * What each room in a map is *for*.
 *
 * The compiler knows a space is six by nine metres, that it is a `room` rather
 * than a `lane`, and that its author called it "Loading". It does not know
 * that a loading bay has pallets in it, a roller door at one end, and a strip
 * of hazard paint on the floor — and it should not: that is presentation, and
 * putting it in the spec would turn a level format into a set-dressing format.
 *
 * So the place grammar sits between them. It reads what the spec already says
 * — the role, the label, the shape, the map's own architecture — and decides
 * what kind of place each space is. Everything downstream dresses a *use*
 * rather than a rectangle, which is the difference between a room with a
 * function and a room with props scattered in it.
 *
 * It is deterministic. The same map always produces the same places, so a
 * screenshot means something and a bug can be found twice.
 */

import type { CompiledLevel, LevelSpec, SpaceSpec } from 'levelspec';

/** What a space is for. Dressing recipes are written against these. */
export type PlaceUse =
  | 'concourse'   // where people arrive and stand about
  | 'hall'        // a big room with a purpose nobody wrote down
  | 'office'      // desks, dividers, a printer nobody maintains
  | 'workshop'    // benches, racks, things half taken apart
  | 'store'       // shelving and stock
  | 'plant'       // pipes, tanks, switchgear
  | 'yard'        // outside, hard standing, things parked on it
  | 'market'      // stalls, awnings, produce
  | 'tunnel'      // services, cable trays, low light
  | 'stair'       // vertical circulation
  | 'terrace'     // outside and up
  | 'bay'         // loading, roller door, pallets
  | 'checkpoint'  // barriers, a desk, a sign telling you to stop
  | 'archive'     // rows of shelving, one aisle
  | 'garage'      // vehicles, oil, tools
  | 'canteen';    // tables, a servery, a noticeboard

/** The idiom a whole map is dressed in, when the space itself has no opinion. */
export type Interior = 'industrial' | 'civic' | 'market' | 'transit' | 'lab' | 'domestic';

export interface Place {
  id: string;
  /** The space this came from. A big space becomes several places. */
  space: string;
  label: string;
  layer: string;
  role: string;
  use: PlaceUse;
  /** Occupied cells, as `x,y` keys in grid coordinates. */
  cells: Set<string>;
  /** World-space bounds in canonical (x, y) metres. */
  min: [number, number];
  max: [number, number];
  /** Canonical floor and ceiling elevations, in metres. */
  floor: number;
  ceiling: number;
  area: number;
  /** Longest dimension over shortest: a corridor is long and thin. */
  slenderness: number;
}

// -----------------------------------------------------------------------------

/**
 * The strongest signal is the name the author gave it.
 *
 * These maps are traced from real ones and the areas keep their real names —
 * "Long Doors", "Palace", "Boiler Room", "Loading". A person calling a room
 * Loading has already decided what is in it, and no amount of shape analysis
 * beats reading the label.
 */
const BY_NAME: [RegExp, PlaceUse][] = [
  [/loading|dock|freight|goods|delivery/i, 'bay'],
  [/garage|motor|vehicle|truck|car\b|bus\b|depot/i, 'garage'],
  [/kitchen|canteen|mess|cafe|dining|bar\b|restaurant/i, 'canteen'],
  [/office|admin|clerk|desk|reception|lobby|foyer/i, 'office'],
  [/archive|record|library|file|vault|store ?room/i, 'archive'],
  [/store|storage|stock|supply|warehouse|cellar|pantry/i, 'store'],
  [/workshop|works|shop floor|assembly|repair|forge|smith/i, 'workshop'],
  [/boiler|plant|pump|generator|turbine|reactor|electr|switch|utility|machine/i, 'plant'],
  [/market|souk|bazaar|stall|shop|arcade|trader/i, 'market'],
  [/yard|court|plaza|square|forecourt|apron|lot\b|garden|park\b/i, 'yard'],
  [/tunnel|sewer|drain|duct|service|crawl|under/i, 'tunnel'],
  [/stair|steps|ramp|lift|elevator|core\b/i, 'stair'],
  [/roof|terrace|balcon|deck|catwalk|gantry|walkway/i, 'terrace'],
  [/gate|checkpoint|entry|entrance|guard|security|control/i, 'checkpoint'],
  [/hall|atrium|concourse|platform|station|nave|main/i, 'concourse'],
];

/** Failing a name, the map's idiom decides what an ordinary room is. */
const BY_INTERIOR: Record<Interior, PlaceUse[]> = {
  industrial: ['workshop', 'plant', 'store', 'bay'],
  civic: ['office', 'hall', 'archive', 'concourse'],
  market: ['market', 'store', 'yard', 'canteen'],
  transit: ['concourse', 'store', 'plant', 'office'],
  lab: ['office', 'plant', 'archive', 'workshop'],
  domestic: ['hall', 'canteen', 'store', 'office'],
};

/**
 * Which idiom a map is in, from the architecture it was already given.
 *
 * There is no separate table for this and there should not be: a map whose
 * surrounding blocks are sheds and silos and whose facades are ribbed steel
 * has already said what its interiors are like, and saying it twice is two
 * things to keep in step.
 *
 * Scored rather than matched in order, and the buildings you are standing
 * among count for more than the ones on the horizon — an office block with a
 * chimney somewhere behind it is an office block, and a first-match rule read
 * the chimney and dressed the whole map as a foundry.
 */
const SIGNALS: [Interior, string[], string[]][] = [
  // interior, blocks that say so, skyline that says so
  ['industrial', ['shed', 'sawtooth', 'silo', 'barrel', 'gantry'], ['chimney', 'crane', 'cooling', 'pylon', 'silo']],
  ['transit',    ['signboard', 'pagoda', 'arcade'], ['signboard', 'pagoda', 'mast']],
  ['lab',        ['glassbox', 'drum', 'dome', 'setback'], ['dome', 'mast', 'obelisk']],
  ['civic',      ['tower', 'terrace', 'helipad', 'chamfer'], ['campanile', 'obelisk', 'dome']],
  ['market',     ['arcade', 'gable', 'barrel_vault', 'courtyard'], ['minaret', 'campanile', 'water_tank']],
  ['domestic',   ['gable', 'pitched', 'rowhouse'], ['water_tank']],
];

export function interiorFor(
  facade: string | undefined,
  blocks: string[] | undefined,
  skyline: string[] | undefined,
): Interior {
  const score = new Map<Interior, number>();
  const bump = (i: Interior, n: number): void => { score.set(i, (score.get(i) ?? 0) + n); };
  for (const [interior, wantBlocks, wantSky] of SIGNALS) {
    for (const b of blocks ?? []) if (wantBlocks.includes(b)) bump(interior, 2);
    for (const k of skyline ?? []) if (wantSky.includes(k)) bump(interior, 1);
  }
  if (facade === 'industrial') bump('industrial', 3);
  if (facade === 'banded') bump('civic', 2);
  if (facade === 'punched') bump('market', 1);
  let best: Interior = 'domestic';
  let top = 0;
  for (const [interior, n] of score) if (n > top) { top = n; best = interior; }
  return best;
}

/**
 * What an open lane is for, in a map of this kind.
 *
 * Most of the library is traced from radars, and a radar trace is three or
 * four big open areas with no names — so the outdoors is most of what a player
 * sees, and dressing all of it the same way is most of what made every map
 * look like every other map.
 */
const OUTDOORS: Record<Interior, PlaceUse[]> = {
  industrial: ['bay', 'yard', 'plant', 'store'],
  civic: ['yard', 'concourse', 'checkpoint'],
  market: ['market', 'yard', 'store', 'canteen'],
  transit: ['concourse', 'market', 'yard', 'checkpoint'],
  lab: ['yard', 'checkpoint', 'plant'],
  domestic: ['market', 'yard', 'canteen'],
};

/** North, south, east, west — for naming a place nobody named. */
const COMPASS = ['SOUTH', 'WEST', 'NORTH', 'EAST'];

const USE_WORD: Record<PlaceUse, string> = {
  concourse: 'CONCOURSE', hall: 'HALL', office: 'OFFICES', workshop: 'WORKSHOP',
  store: 'STORES', plant: 'PLANT', yard: 'YARD', market: 'MARKET', tunnel: 'SERVICE',
  stair: 'STAIRS', terrace: 'TERRACE', bay: 'LOADING', checkpoint: 'CHECKPOINT',
  archive: 'ARCHIVE', garage: 'GARAGE', canteen: 'CANTEEN',
};

/** A trace names its regions "Area B", which is not a name. */
const UNNAMED = /^\s*(area|region|zone|space)\s*[a-z0-9]?\s*$/i;

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function cellsOf(space: SpaceSpec): Set<string> {
  const cells = new Set<string>();
  const add = (r: number[]): void => {
    for (let j = 0; j < r[3]; j++) for (let i = 0; i < r[2]; i++) cells.add(`${r[0] + i},${r[1] + j}`);
  };
  const cut = (r: number[]): void => {
    for (let j = 0; j < r[3]; j++) for (let i = 0; i < r[2]; i++) cells.delete(`${r[0] + i},${r[1] + j}`);
  };
  add(space.rect);
  for (const r of space.extra ?? []) add(r);
  for (const r of space.subtract ?? []) cut(r);
  return cells;
}

/**
 * Read the place grammar off a compiled level.
 *
 * Roles that already say what they are — a stairwell, a balcony, the outside —
 * keep their answer. Everything else is decided by its name, and failing that
 * by the map's idiom and its own shape: a long thin room is a corridor whatever
 * it is called, and a corridor is a tunnel or a concourse depending on whether
 * the map is a factory or a station.
 */
export function derivePlaces(
  spec: LevelSpec,
  level: CompiledLevel,
  interior: Interior,
  seed: string,
): Place[] {
  const g = spec.grid ?? 1;
  const out: Place[] = [];

  for (const layer of spec.layers) {
    for (const space of layer.spaces) {
      const cells = cellsOf(space);
      if (!cells.size) continue;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const k of cells) {
        const c = k.indexOf(',');
        const x = Number(k.slice(0, c));
        const y = Number(k.slice(c + 1));
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x + 1 > maxX) maxX = x + 1;
        if (y + 1 > maxY) maxY = y + 1;
      }
      const w = (maxX - minX) * g;
      const d = (maxY - minY) * g;
      const label = space.label ?? space.id.replace(/_/g, ' ');
      const role = space.role ?? 'room';

      let use: PlaceUse | null = null;
      if (role === 'stairwell') use = 'stair';
      else if (role === 'balcony' || role === 'roof') use = 'terrace';
      else if (role === 'exterior') use = 'yard';
      if (!use) for (const [re, u] of BY_NAME) if (re.test(label) || re.test(space.id)) { use = u; break; }
      if (!use) {
        const slender = Math.max(w, d) / Math.max(0.001, Math.min(w, d));
        // A long thin space is a route whatever the label says, and a route is
        // a service tunnel in a factory and a concourse in a station.
        if (role === 'corridor' || slender > 3.4) {
          use = interior === 'industrial' ? 'tunnel' : interior === 'transit' ? 'concourse' : 'tunnel';
        } else if (role === 'lane') {
          use = interior === 'market' ? 'market' : 'yard';
        } else {
          const pool = BY_INTERIOR[interior];
          use = pool[hash(`${seed}:${space.id}`) % pool.length];
        }
      }

      const floor = layer.z + (space.z_offset ?? 0);
      const ceiling = floor + (space.height ?? layer.height);
      const area = cells.size * g * g;
      const named = !UNNAMED.test(label);

      /*
       * A big open area is more than one place.
       *
       * A radar trace gives three or four regions of a thousand square metres
       * each, and dressing a thousand square metres as one thing is what makes
       * a street read as a corridor with objects in it. So anything over about
       * four hundred is cut into zones — by position, so the cut lands
       * somewhere a person would draw one — and each zone gets its own use and
       * its own name. A long street becomes a market end and a service end,
       * which is what a long street is.
       */
      const zones = named ? 1 : Math.max(1, Math.min(4, Math.round(area / 420)));
      const pool = role === 'lane' || role === 'exterior' ? OUTDOORS[interior] : BY_INTERIOR[interior];
      const groups: Set<string>[] = Array.from({ length: zones }, () => new Set<string>());
      if (zones === 1) {
        for (const k of cells) groups[0].add(k);
      } else {
        // Cut along whichever way the space is longer, into equal shares of
        // its extent rather than of its area: a lane that widens at one end
        // should still be cut where it changes, not where the count evens out.
        const along = w >= d ? 0 : 1;
        const lo = along === 0 ? minX : minY;
        const span = Math.max(1, (along === 0 ? maxX - minX : maxY - minY));
        for (const k of cells) {
          const c = k.indexOf(',');
          const v = along === 0 ? Number(k.slice(0, c)) : Number(k.slice(c + 1));
          groups[Math.min(zones - 1, Math.floor(((v - lo) / span) * zones))].add(k);
        }
      }

      groups.forEach((group, gi) => {
        if (!group.size) return;
        let zx0 = Infinity, zy0 = Infinity, zx1 = -Infinity, zy1 = -Infinity;
        let sx = 0, sy = 0;
        for (const k of group) {
          const c = k.indexOf(',');
          const x = Number(k.slice(0, c));
          const y = Number(k.slice(c + 1));
          zx0 = Math.min(zx0, x); zy0 = Math.min(zy0, y);
          zx1 = Math.max(zx1, x + 1); zy1 = Math.max(zy1, y + 1);
          sx += x; sy += y;
        }
        const zoneUse = zones === 1 ? use! : pool[(hash(`${seed}:${space.id}:${gi}`) + gi) % pool.length];
        // Named by where it is and what it is, when nobody named it. A sign
        // reading "AREA B" is worse than no sign; one reading "NORTH YARD" is
        // a thing a person can navigate by.
        const cx = sx / group.size - (minX + maxX) / 2;
        const cy = sy / group.size - (minY + maxY) / 2;
        const quad = Math.abs(cx) > Math.abs(cy) ? (cx > 0 ? 3 : 1) : (cy > 0 ? 2 : 0);
        const zoneLabel = named
          ? label
          : `${zones > 1 ? `${COMPASS[quad]} ` : ''}${USE_WORD[zoneUse]}`;
        out.push({
          id: zones === 1 ? space.id : `${space.id}#${gi}`,
          space: space.id,
          label: zoneLabel,
          layer: layer.id,
          role,
          use: zoneUse,
          cells: group,
          min: [zx0 * g, zy0 * g],
          max: [zx1 * g, zy1 * g],
          floor,
          ceiling,
          area: group.size * g * g,
          slenderness: Math.max(w, d) / Math.max(0.001, Math.min(w, d)),
        });
      });
    }
  }
  void level;
  return out;
}
